import { readUnknownProperty } from "@shared/validation/narrow";

export interface DistributableColumn {
  id: string;
  size: number;
  growMaxWidth: number;
}

export const FLEXIBLE_COLUMN_MAX_WIDTH = 320;

export interface RecordColumnMeta {
  growMaxWidth?: number;
}

export function resolveGrowMaxWidth(meta: unknown): number {
  const declared = readUnknownProperty(meta, "growMaxWidth");
  return typeof declared === "number" ? declared : 0;
}

export function flexibleWidth(
  meta?: object,
  growMaxWidth: number = FLEXIBLE_COLUMN_MAX_WIDTH,
): Record<string, unknown> {
  return { ...meta, growMaxWidth };
}

export function distributeColumnWidths(
  columns: DistributableColumn[],
  available: number,
): Map<string, number> {
  const widths = new Map(columns.map((column) => [column.id, column.size]));
  const declared = columns.reduce((total, column) => total + column.size, 0);
  let slack = available - declared;
  if (slack <= 0) return widths;

  let growable = columns.filter((column) => column.growMaxWidth > column.size);
  let passes = columns.length;
  while (slack > 0 && growable.length > 0 && passes-- > 0) {
    const weight = growable.reduce((total, column) => total + column.size, 0);
    if (weight <= 0) break;

    const pass = slack;
    const stillGrowable: DistributableColumn[] = [];
    let claimed = 0;

    for (const column of growable) {
      const current = widths.get(column.id) ?? column.size;
      const grown = Math.min(
        current + Math.floor((pass * column.size) / weight),
        column.growMaxWidth,
      );
      claimed += grown - current;
      widths.set(column.id, grown);
      if (grown < column.growMaxWidth) stillGrowable.push(column);
    }

    if (claimed === 0) break;
    slack -= claimed;
    growable = stillGrowable;
  }

  return widths;
}
